import { create } from '@thoughtspot/logger';
import {
    MetricsService,
    PropertyMap,
    UserAction,
    userActionTypeEnum,
} from '@thoughtspot/metrics';
import type {
    AddColumnType,
    SheetKind,
    SpreadsheetTelemetryEvent,
} from '@thoughtspot/spreadsheet';
import type { SpreadsheetAnswerContext } from './spreadsheet-answer-context';

const Logger = create('spreadsheet-mixpanel-util');

const trackSpreadsheetEvent = (
    action: typeof userActionTypeEnum[keyof typeof userActionTypeEnum],
    data: PropertyMap,
    metricsService: MetricsService,
) => {
    try {
        metricsService.collectEvent(new UserAction(action), data);
    } catch (error) {
        Logger.error(error, `Failed to track ${action} event`);
    }
};

export type SpreadsheetEntryPoint =
    | 'home_nav' // keeping it as per prd for now, will be a follow up task to look into in phase 2
    | 'search_data'
    | 'answer_reopen';

// Extract all the functions from the tokens
export const formulaFunctionNames = (
    tokens: ReadonlyArray<{ type?: string | null; text?: string | null }>,
): string[] => [
    ...new Set(
        tokens
            .filter(token => token.type === 'FUNCTION_NAME')
            .map(token => token.text?.toLowerCase())
            .filter((text): text is string => !!text),
    ),
];

// A save transition flips the session to saved without the user having opened
// anything — either an in-place isSaved flip or the first-save route remount on
// the flag-off path. Either would otherwise read as a fresh open.
let suppressNextOpenedEvent = false;

/** Call immediately before a save transition, so the open it triggers is not
 *  reported as a user-initiated open. */
export const suppressNextSpreadsheetOpenedEvent = (): void => {
    suppressNextOpenedEvent = true;
};

/** True once per suppression, then rearms. Callers report the open when this
 *  returns false. */
export const consumeSpreadsheetOpenedSuppression = (): boolean => {
    if (!suppressNextOpenedEvent) {
        return false;
    }
    suppressNextOpenedEvent = false;
    return true;
};

export const trackSpreadsheetOpened = (
    data: {
        entryPoint: SpreadsheetEntryPoint;
        answerContext: SpreadsheetAnswerContext;
    },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_OPENED,
        {
            entry_point: data.entryPoint,
            answer_id: data.answerContext.answerId,
            model_guid: data.answerContext.modelGuid,
        },
        metricsService,
    );

// Tracks the Query/Spreadsheet toggle click on the answer screen — a UI
// control interaction, distinct from spreadsheet-opened (arrival in
// spreadsheet mode by any means, e.g. redirects or direct URLs).
export const trackSpreadsheetToggleClick = (metricsService: MetricsService) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_TOGGLE_CLICK,
        {},
        metricsService,
    );

export const trackSpreadsheetFormulaCreated = (
    data: {
        /** The resolved column type — MEASURE / ATTRIBUTE / UNKNOWN. */
        formulaType: string;
        columnCount: number;
        formulaFunctions: string[];
        columnIndex: number;
    },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_FORMULA_CREATED,
        {
            formula_type: data.formulaType,
            column_count: data.columnCount,
            formula_functions: data.formulaFunctions,
            column_index: data.columnIndex,
        },
        metricsService,
    );

export const trackSpreadsheetSaved = (
    data: {
        columnCount: number;
        formulaCount: number;
        saveType: 'new' | 'overwrite';
        hasSort: boolean;
        answerContext: SpreadsheetAnswerContext;
    },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_SAVED,
        {
            column_count: data.columnCount,
            formula_count: data.formulaCount,
            save_type: data.saveType,
            has_sort: data.hasSort,
            has_filter: data.answerContext.hasFilter,
            has_conditional_format: data.answerContext.hasConditionalFormat,
        },
        metricsService,
    );

export const trackSpreadsheetExported = (
    data: { exportFormat: 'CSV' | 'XLSX'; rowCount: number },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_EXPORTED,
        {
            export_format: data.exportFormat,
            row_count: data.rowCount,
        },
        metricsService,
    );

export const trackSpreadsheetColumnAdded = (
    data: { columnType: 'formula' | 'data'; columnIndex: number },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_COLUMN_ADDED,
        {
            column_type: data.columnType,
            column_index: data.columnIndex,
        },
        metricsService,
    );

export const trackSpreadsheetColumnDeleted = (
    data: { hadFormula: boolean },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_COLUMN_DELETED,
        { had_formula: data.hadFormula },
        metricsService,
    );

export const trackSpreadsheetFormulaEdited = (
    data: { formulaFunctions: string[] },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_FORMULA_EDITED,
        { formula_functions: data.formulaFunctions },
        metricsService,
    );

/**
 * Routes an event raised inside the spreadsheet package onto Mixpanel.
 */
export const trackSpreadsheetPackageEvent = (
    event: SpreadsheetTelemetryEvent,
    metricsService: MetricsService,
) => {
    if (event.name === 'sort-applied') {
        trackSpreadsheetEvent(
            userActionTypeEnum.SPREADSHEET_SORT_APPLIED,
            event.props,
            metricsService,
        );
    }
};

export const trackSpreadsheetFullscreenChange = (
    data: { isFullscreen: boolean },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        data.isFullscreen
            ? userActionTypeEnum.SPREADSHEET_FULL_SCREEN_ENTERED
            : userActionTypeEnum.SPREADSHEET_FULL_SCREEN_EXITED,
        {},
        metricsService,
    );

export enum SpreadsheetInputTableState {
    New = 'new',
    Saved = 'saved',
}

export enum SpreadsheetInputDiscardReason {
    Navigation = 'navigation',
    TabClose = 'tab_close',
}

const columnDataType = (type: AddColumnType): string => type.toLowerCase();

// A total record, so a new SheetKind is a compile error here rather than a
// silently mangled event value.
const TAB_TYPE: Readonly<Record<SheetKind, string>> = {
    answer: 'answer',
    inputTable: 'input_table',
};

const tabType = (kind: SheetKind): string => TAB_TYPE[kind];

export const trackSpreadsheetInputTabCreated = (
    data: { existingTabCount: number },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_INPUT_TAB_CREATED,
        { existing_tab_count: data.existingTabCount },
        metricsService,
    );

export const trackSpreadsheetInputTabSwitched = (
    data: {
        fromTabType: SheetKind;
        toTabType: SheetKind;
        fromTabIndex: number;
        toTabIndex: number;
    },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_INPUT_TAB_SWITCHED,
        {
            from_tab_type: tabType(data.fromTabType),
            to_tab_type: tabType(data.toTabType),
            from_tab_index: data.fromTabIndex,
            to_tab_index: data.toTabIndex,
        },
        metricsService,
    );

export const trackSpreadsheetInputTabClosed = (
    data: {
        tabHadData: boolean;
        wasSaved: boolean;
        inputColumnCount: number;
    },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_INPUT_TAB_CLOSED,
        {
            tab_had_data: data.tabHadData,
            was_saved: data.wasSaved,
            input_column_count: data.inputColumnCount,
        },
        metricsService,
    );

export const trackSpreadsheetInputTabOpenedFromPanel = (
    data: { inputTableId: string; tabAlreadyOpen: boolean },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_INPUT_TAB_OPENED_FROM_PANEL,
        {
            input_table_id: data.inputTableId,
            tab_already_open: data.tabAlreadyOpen,
        },
        metricsService,
    );

export const trackSpreadsheetInputTabRenamed = (
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_INPUT_TAB_RENAMED,
        {},
        metricsService,
    );

export const trackSpreadsheetInputKeyColumnSelected = (
    data: {
        columnCategory: string;
        isSelected: boolean;
        gridRowCount: number;
    },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_INPUT_KEY_COLUMN_SELECTED,
        {
            column_category: data.columnCategory,
            is_selected: data.isSelected,
            grid_row_count: data.gridRowCount,
        },
        metricsService,
    );

export const trackSpreadsheetInputColumnAdded = (
    data: {
        columnDataType: AddColumnType;
        tableState: SpreadsheetInputTableState;
    },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_INPUT_COLUMN_ADDED,
        {
            column_data_type: columnDataType(data.columnDataType),
            table_state: data.tableState,
        },
        metricsService,
    );

export const trackSpreadsheetInputColumnRenamed = (
    data: { columnDataType: AddColumnType },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_INPUT_COLUMN_RENAMED,
        { column_data_type: columnDataType(data.columnDataType) },
        metricsService,
    );

export const trackSpreadsheetInputColumnTypeChanged = (
    data: {
        oldDataType: AddColumnType;
        newDataType: AddColumnType;
        valuesPresent: number;
    },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_INPUT_COLUMN_TYPE_CHANGED,
        {
            old_data_type: columnDataType(data.oldDataType),
            new_data_type: columnDataType(data.newDataType),
            values_present: data.valuesPresent,
        },
        metricsService,
    );

export const trackSpreadsheetDraftColumnDiscarded = (
    data: {
        columnDataType: AddColumnType;
        hadValues: boolean;
        tableState: SpreadsheetInputTableState;
    },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_DRAFT_COLUMN_DISCARDED,
        {
            column_data_type: columnDataType(data.columnDataType),
            had_values: data.hadValues,
            table_state: data.tableState,
        },
        metricsService,
    );

export const trackSpreadsheetInputSessionDiscarded = (
    data: { reason: SpreadsheetInputDiscardReason; rowsEntered: number },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_INPUT_SESSION_DISCARDED,
        { reason: data.reason, rows_entered: data.rowsEntered },
        metricsService,
    );

/** Which prism op a writeback failure came from. */
export enum SpreadsheetInputSaveOp {
    Create = 'create',
    AddColumns = 'add_columns',
    UpdateValues = 'update_values',
    DeleteColumn = 'delete_column',
}

export const trackSpreadsheetInputTableCreated = (
    data: {
        inputTableId: string;
        inputColumnCount: number;
        referencedColumnCount: number;
    },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_INPUT_TABLE_CREATED,
        {
            input_table_id: data.inputTableId,
            input_column_count: data.inputColumnCount,
            referenced_column_count: data.referencedColumnCount,
        },
        metricsService,
    );

export const trackSpreadsheetInputTableValuesSaved = (
    data: {
        inputTableId: string;
        rowsWritten: number;
        columnCount: number;
    },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_INPUT_TABLE_VALUES_SAVED,
        {
            input_table_id: data.inputTableId,
            rows_written: data.rowsWritten,
            column_count: data.columnCount,
        },
        metricsService,
    );

export const trackSpreadsheetInputColumnsAdded = (
    data: {
        inputTableId: string;
        columnsAdded: number;
        columnDataTypes: string[];
    },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_INPUT_COLUMNS_ADDED,
        {
            input_table_id: data.inputTableId,
            columns_added: data.columnsAdded,
            column_data_types: data.columnDataTypes,
        },
        metricsService,
    );

export const trackSpreadsheetInputTableSaveFailed = (
    data: {
        inputTableId: string | null;
        op: SpreadsheetInputSaveOp;
        errorMessage?: string;
    },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_INPUT_TABLE_SAVE_FAILED,
        {
            input_table_id: data.inputTableId ?? '',
            op: data.op,
            error_message: data.errorMessage ?? '',
        },
        metricsService,
    );

export const trackSpreadsheetInputColumnDeleted = (
    data: { inputTableId: string; columnGuid: string },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_INPUT_COLUMN_DELETED,
        {
            input_table_id: data.inputTableId,
            column_guid: data.columnGuid,
        },
        metricsService,
    );

export const trackSpreadsheetInputTableDeleted = (
    data: { inputTableId: string },
    metricsService: MetricsService,
) =>
    trackSpreadsheetEvent(
        userActionTypeEnum.SPREADSHEET_INPUT_TABLE_DELETED,
        { input_table_id: data.inputTableId },
        metricsService,
    );
